import { switchActiveWebsite } from '@va/dashboard/actions/app';
import { getInstanceId } from '@va/dashboard/selectors/app';
import { ErrorIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { WebsiteListItemComponent } from '@va/standalone/shared/feature-website-selector';
import { useGetWebsites } from '@va/standalone/shared/hooks';
import { WebsiteType } from '@va/types/website';
import { Paragraph, SearchField } from '@va/ui/design-system';
import { getUrlWithoutProtocolAndTrailingSlash } from '@va/util/helpers';
import { useInnerOnChange } from '@va/util/hooks';
import classNames from 'classnames';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';

const PAGE_SIZE = 50;

export const WebsiteSelectorDropdown = ({ closeDropdown, userId }: { closeDropdown: () => void; userId: string }) => {
  const [pageData, setPageData] = useState<Record<number, WebsiteType[]>>([]);
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(0);
  const [controller, setController] = useState(new AbortController());

  const onSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setController((prev) => {
      prev.abort();
      return new AbortController();
    });
    setPageData({});
    setPage(0);
    setSearchValue(e.target.value);
  }, []);

  const { innerOnChange, innerValue } = useInnerOnChange(
    (e) => {
      onSearch(e);
    },
    200,
    '',
  );

  const translate = useTranslate();
  const dispatch = useDispatch();
  const instanceId = useSelector(getInstanceId);

  const clearSearchField = useCallback(() => {
    innerOnChange({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>);
  }, [innerOnChange]);

  const shouldBlockPageChange = useRef(false);

  const {
    data: lastResponse,
    error,
    isValidating,
  } = useGetWebsites(
    controller,
    { url: searchValue, length: PAGE_SIZE, start: page * PAGE_SIZE, withSharedWebsites: true },
    {
      onSuccess: () => {
        setTimeout(() => {
          shouldBlockPageChange.current = false;
        }, 100);
      },
    },
    userId,
  );

  useEffect(() => {
    if (!lastResponse) return;
    setPageData((prev) => {
      const page = Math.floor(lastResponse.start / PAGE_SIZE);
      return { ...prev, [page]: lastResponse.websites };
    });
  }, [lastResponse]);

  const websites = useMemo(() => {
    let list: WebsiteType[] = [];
    for (const key in pageData) {
      list = list.concat(pageData[key]);
    }
    return list;
  }, [pageData]);

  const observer = useRef<IntersectionObserver | null>(null);

  const lastElementRef = useCallback(
    (node: any) => {
      if (!node) return;
      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        if (shouldBlockPageChange.current) return;
        if (!entries[0].isIntersecting) return;
        if (!lastResponse) return;
        const hasMore = websites.length < lastResponse?.total;

        if (!hasMore) return;

        setPage((prev) => {
          return prev + 1;
        });
        shouldBlockPageChange.current = true;
      });

      observer.current.observe(node);
    },
    [lastResponse, websites.length],
  );

  return (
    <div className={classNames('text-gray-charcoal p-2 rounded-18 bg-white border-2 border-gray-concrete')}>
      <div className='bg-white pb-2'>
        <SearchField
          size='small'
          className='h-10'
          value={innerValue as string}
          onChange={innerOnChange}
          clearField={clearSearchField}
        />
      </div>
      <ul className='flex flex-col gap-2 h-40 overflow-y-auto scrollbar scrollbar-thumb'>
        {websites?.length === 0 && !isValidating && (
          <div className='w-full h-full flex items-center justify-center '>
            <Paragraph>{translate('labels.noWebsites')}</Paragraph>
          </div>
        )}
        {websites.map((website, index) => {
          const url = getUrlWithoutProtocolAndTrailingSlash(website.url);

          const commonProps = {
            onWebsiteSelect: () => {
              dispatch(switchActiveWebsite({ websiteId: website.id }));
            },
            closeDropdown: closeDropdown,
            websiteId: website.id,
            url: url,
            activeWebsiteId: instanceId,
          };

          if (index === websites.length - 1)
            return <WebsiteListItemComponent key={website.id} ref={lastElementRef} {...commonProps} />;
          return <WebsiteListItemComponent key={website.id} {...commonProps} />;
        })}
        {isValidating && <Skeleton count={4} className='rounded-15 h-12 mb-1' />}
        {error && (
          <div className='w-full min-h-10 flex items-center justify-center gap-3'>
            <ErrorIcon className='w-5 h-5' color='var(--color-negative)' />
            <Paragraph className='text-negative'>{translate('notifications.generalError')}</Paragraph>
          </div>
        )}
      </ul>
    </div>
  );
};
